import type { UnauthorizedReason } from '../../utilities/errors/HTTPErrors';

/**
 * Lightweight pub/sub that decouples *detecting* a session-expiry 401 (e.g. in
 * the React Query cache, far from React) from *reacting* to it (showing a
 * message and signing out inside a React component).
 *
 * Notifications are throttled so a burst of simultaneous 401s (a dashboard
 * firing many queries at once) results in a single sign-out flow.
 */
export type SessionExpiredListener = (reason: UnauthorizedReason) => void;

const DEFAULT_THROTTLE_MS = 5000;

const listeners = new Set<SessionExpiredListener>();
// Negative infinity so the very first notification is never throttled.
let lastNotifiedAt = Number.NEGATIVE_INFINITY;

/**
 * Registers a listener invoked when a session-expiry is detected.
 * @returns An unsubscribe function.
 */
export function onSessionExpired(listener: SessionExpiredListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/**
 * Announces that the user's session has expired/become invalid.
 * Throttled to avoid duplicate sign-out flows from concurrent failures.
 */
export function notifySessionExpired(
  reason: UnauthorizedReason,
  options?: { throttleMs?: number; now?: number },
): void {
  const now = options?.now ?? Date.now();
  const throttleMs = options?.throttleMs ?? DEFAULT_THROTTLE_MS;

  if (now - lastNotifiedAt < throttleMs) {
    return;
  }
  lastNotifiedAt = now;

  listeners.forEach((listener) => listener(reason));
}

/** Test-only: clears listeners and resets the throttle window. */
export function resetSessionExpiryCoordinator(): void {
  listeners.clear();
  lastNotifiedAt = Number.NEGATIVE_INFINITY;
}
